import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { Retailer } from '../../api/contracts/Retailer';
import type { JobDetailsResponse } from '../../api/contracts/JobsResponse';

export const RETAILERS = Object.values(Retailer).map((retailer) => ({
  id: retailer,
}));

interface StartJobDialogProps {
  open: boolean;
  onClose: () => void;
  onStart: (retailers: string[], status?: string, limit?: number) => void;
  retryJob?: JobDetailsResponse;
}

export const StartJobDialog = ({
  open,
  onClose,
  onStart,
  retryJob,
}: StartJobDialogProps) => {
  const [selectedRetailers, setSelectedRetailers] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [limit, setLimit] = useState<string>('');
  const [limitError, setLimitError] = useState<string>('');

  useEffect(() => {
    if (retryJob) {
      // Pre-select retailers that have failed products
      const failedRetailers = retryJob.retailers
        .filter((r) => r.productsFailed > 0)
        .map((r) => r.retailer);
      setSelectedRetailers(failedRetailers);
    } else {
      setSelectedRetailers([]);
    }
    setIsSubmitting(false);
    setLimit('');
    setLimitError('');
  }, [retryJob, open]);

  const handleRetailerToggle = (retailer: string) => {
    setSelectedRetailers((prev) =>
      prev.includes(retailer)
        ? prev.filter((r) => r !== retailer)
        : [...prev, retailer]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRetailers(
      checked ? RETAILERS.map((retailer) => retailer.id) : []
    );
  };

  const validateLimit = (value: string): boolean => {
    if (value === '') {
      setLimitError('');
      return true;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
      setLimitError('Please enter a valid number');
      return false;
    }

    if (numValue <= 0) {
      setLimitError('Limit must be a positive number');
      return false;
    }

    setLimitError('');
    return true;
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLimit(value);
    validateLimit(value);
  };

  const handleSubmit = () => {
    if (limit && !validateLimit(limit)) {
      return;
    }

    setIsSubmitting(true);
    onStart(
      selectedRetailers,
      retryJob ? 'failed' : undefined,
      limit ? Number(limit) : undefined
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ style: { width: '400px' } }}
    >
      <DialogTitle>{retryJob ? 'Retry Job' : 'Start New Job'}</DialogTitle>
      <DialogContent>
        <FormGroup>
          {!retryJob && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedRetailers.length === RETAILERS.length}
                  indeterminate={
                    selectedRetailers.length > 0 &&
                    selectedRetailers.length < RETAILERS.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              }
              label="Select All"
            />
          )}
          {RETAILERS.map((retailer) => {
            // For retry jobs, check retailer status
            if (retryJob) {
              const retailerStatus = retryJob.retailers.find(
                (r) => r.retailer === retailer.id
              );
              const hasFailedProducts = retailerStatus?.productsFailed ?? 0 > 0;
              if (!hasFailedProducts) {
                return null;
              }

              return (
                <FormControlLabel
                  key={retailer.id}
                  control={
                    <Checkbox
                      checked={selectedRetailers.includes(retailer.id)}
                      onChange={() => handleRetailerToggle(retailer.id)}
                    />
                  }
                  label={`${retailer.id}${hasFailedProducts ? ` (${retailerStatus?.productsFailed ?? 0} failed)` : ''}`}
                />
              );
            }

            // For new jobs, show all retailers
            return (
              <FormControlLabel
                key={retailer.id}
                control={
                  <Checkbox
                    checked={selectedRetailers.includes(retailer.id)}
                    onChange={() => handleRetailerToggle(retailer.id)}
                  />
                }
                label={retailer.id}
              />
            );
          })}

          {!retryJob && (
            <>
              <TextField
                margin="normal"
                fullWidth
                label="Products limit (optional)"
                type="number"
                value={limit}
                onChange={handleLimitChange}
                error={!!limitError}
                helperText={limitError}
                InputProps={{
                  inputProps: {
                    min: 1,
                    step: 1,
                    inputMode: 'numeric',
                  },
                }}
              />
            </>
          )}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            selectedRetailers.length === 0 || isSubmitting || !!limitError
          }
        >
          {isSubmitting ? 'Processing...' : retryJob ? 'Retry' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
